import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { UserDetails } from '../../users/models/UserDetails';
import * as fromAuth from '../../../store/auth';

export interface LocationWithoutAlertRecipient {
  customerId: number;
  locationId: number;
}

@Injectable({
  providedIn: 'root',
})
export class UsersManagementService {
  private alertRecipientsChanged$ = new BehaviorSubject<void>(undefined);

  // Every location the signed-in executive manages where nobody would get a data-issue alert. Fetched
  // once and shared by the header badge and the Users page; fetched again after a user is saved or
  // when someone else signs in (or is impersonated).
  locationsWithoutAlertRecipient$: Observable<LocationWithoutAlertRecipient[]> = combineLatest([
    this.store.select(fromAuth.selectUser).pipe(
      map(user => user?.userId),
      distinctUntilChanged(),
    ),
    this.alertRecipientsChanged$,
  ]).pipe(
    switchMap(([userId]) =>
      userId == null
        ? of([])
        : this.http
            .get<LocationWithoutAlertRecipient[]>(`${environment.apiBase}/customerUsers/alert-recipient-status`)
            .pipe(catchError(() => of([]))),
    ),
    shareReplay(1),
  );

  constructor(
    private http: HttpClient,
    private store: Store<{ auth: fromAuth.AuthState }>,
  ) {}

  getUsers(): Observable<UserDetails[]> {
    return this.http.get<UserDetails[]>(`${environment.apiBase}/customerUsers`);
  }

  createUser(user: UserDetails): Observable<UserDetails> {
    return this.http.post<UserDetails>(`${environment.apiBase}/customerUsers`, user);
  }

  updateUser(user: UserDetails): Observable<UserDetails> {
    return this.http.put<UserDetails>(`${environment.apiBase}/customerUsers/update/${user.userId}`, user);
  }

  blockUser(userId: number) {
    return this.http.put(`${environment.apiBase}/customerUsers/block/${userId}`, null);
  }

  validateEmail(email: string) {
    return this.http.get<boolean>(`${environment.apiBase}/customerUsers/email/available/${email}`);
  }

  refreshAlertRecipientStatus() {
    this.alertRecipientsChanged$.next();
  }
}
